import type { SvgIconProps } from '@mui/material';
import type { FC, ReactNode } from 'react';
import { z } from 'zod';
import type { PresetViewType, Validator } from '@/features/savedPresets';
import type { ViewType } from '@/entities/session';

export interface Dashboard {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  favorite: boolean;
  widgets: Widget[];
}

interface WidgetBase {
  id: string;
  type: PresetViewType;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export interface PresetRefWidget extends WidgetBase {
  presetId: string;
}

export interface EmbeddedPresetWidget extends WidgetBase {
  presetName: string;
  presetConfig: unknown;
}

export type Widget = PresetRefWidget | EmbeddedPresetWidget;

const positionSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const dashboardSnapshotValidator = z.object({
  name: z.string(),
  widgets: z.array(
    z.union([
      z.object({
        id: z.string(),
        type: z.string(),
        presetId: z.string(),
        position: positionSchema,
      }),
      z.object({
        id: z.string(),
        type: z.string(),
        presetName: z.string(),
        presetConfig: z.unknown(),
        position: positionSchema,
      }),
    ]),
  ),
});

export type DashboardSnapshot = Pick<Dashboard, 'name' | 'widgets'>;

export type WidgetsConfig = Record<
  PresetViewType,
  {
    name: string;
    gradientIconUrl: string;
    ViewIcon: FC<SvgIconProps>;
    validator: Validator;
    Wrapper: FC<{ preset: unknown; children: ReactNode }>;
    Component: FC<{ preset: unknown; isReadonly: boolean }>;
    viewType: ViewType;
  }
>;
